<template>
  <div>
    <Navbar />
    <div class="staff-page">

      <!-- Banner -->
      <div class="banner">
        <div class="banner-inner">
          <h1 class="banner-title">Our Staff</h1>
          <p class="banner-subtitle">فريقنا</p>
        </div>
      </div>

      <!-- Department Filter -->
      <div v-if="departments.length > 1" class="filter-bar">
        <button
          :class="['filter-btn', { active: activeDept === '' }]"
          @click="activeDept = ''"
        >All</button>
        <button
          v-for="dept in departments"
          :key="dept"
          :class="['filter-btn', { active: activeDept === dept }]"
          @click="activeDept = dept"
        >{{ dept }}</button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>Loading staff…</p>
      </div>

      <!-- Staff Grid -->
      <div v-else-if="filteredStaff.length" class="staff-grid">
        <div
          v-for="(member, i) in filteredStaff"
          :key="member.id || i"
          class="staff-card"
          :style="{ animationDelay: i * 50 + 'ms' }"
        >
          <div class="card-photo">
            <img
              v-if="member.photo"
              :src="member.photo"
              :alt="member.name"
              class="photo-img"
            />
            <div v-else class="photo-placeholder">
              <span>{{ initials(member.name) }}</span>
            </div>
          </div>

          <div class="card-info">
            <h3 class="member-name">{{ member.name }}</h3>
            <p v-if="member.name_ar" class="member-name-ar">{{ member.name_ar }}</p>
            <p v-if="member.role || member.title" class="member-role">{{ member.role || member.title }}</p>
            <p v-if="member.department" class="member-dept">{{ member.department }}</p>

            <p
              v-if="member.bio && member.bio.trim() && member.bio !== '<p>&nbsp;</p>'"
              class="member-bio"
            >{{ stripHtml(member.bio) }}</p>

            <div class="member-contacts">
              <a v-if="member.phone" :href="'tel:' + member.phone" class="contact-link">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                {{ member.phone }}
              </a>
              <a v-if="member.email" :href="'mailto:' + member.email" class="contact-link email">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,12 2,6"/></svg>
                {{ member.email }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div v-else class="empty-state">
        <p>No staff members found.</p>
      </div>

    </div>
    <Footer />
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue'
import Footer from '@/components/Footer.vue'
import api from '@/services/api'
import { mediaUrl } from '@/services/media'

export default {
  name: 'StaffPage',
  components: { Navbar, Footer },
  data() {
    return { loading: true, allStaff: [], activeDept: '', departments: [] }
  },
  computed: {
    filteredStaff() {
      if (!this.activeDept) return this.allStaff;
      return this.allStaff.filter(s => s.department === this.activeDept);
    }
  },
  async created() {
    document.title = 'Our Staff | Al Shola School';
    try {
      const data = await api.getStaff();
      const list = api.extractList(data);
      if (list.length && list[0].order !== undefined) {
        list.sort((a, b) => (a.order || 0) - (b.order || 0));
      }
      this.allStaff = list.map(s => ({
        ...s,
        photo: s.photo ? mediaUrl(s.photo) : '',
      }));
      const depts = new Set();
      this.allStaff.forEach(s => { if (s.department) depts.add(s.department); });
      this.departments = [...depts].sort();
    } catch (e) {
      console.warn('[Staff] API error:', e.message);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    initials(name = '') {
      return name.split(' ').slice(0, 2).map(w => (w || '')[0]).filter(Boolean).join('').toUpperCase();
    },
    stripHtml(html = '') {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || '';
    }
  }
}
</script>

<style scoped>
/* ─── Reset & Base ─────────────────────────────────────────────── */
* {
  box-sizing: border-box;
}

.staff-page {
  min-height: 60vh;
  background: #fafafa;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  /* CRITICAL FIX: ensure children stack normally, no overflow clipping */
  position: relative;
  overflow: visible;
}

/* ─── Banner ───────────────────────────────────────────────────── */
.banner {
  background: linear-gradient(135deg, #C02130 0%, #8B1722 100%);
  padding: 60px 24px;
  text-align: center;
  /* CRITICAL FIX: no negative margins, no absolute positioning */
  position: relative;
  z-index: 1;
  overflow: visible;
}

.banner-title {
  color: #fff;
  font-size: clamp(28px, 5vw, 42px);
  font-weight: 800;
  margin: 0 0 6px;
}

.banner-subtitle {
  color: rgba(255,255,255,0.85);
  font-size: 20px;
  margin: 0;
  font-family: 'Arial', sans-serif;
}

/* ─── Filter Bar ───────────────────────────────────────────────── */
.filter-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  padding: 32px 24px;
  max-width: 900px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

.filter-btn {
  padding: 8px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 24px;
  background: #fff;
  color: #555;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s;
}

.filter-btn:hover {
  border-color: #C02130;
  color: #C02130;
}

.filter-btn.active {
  background: #C02130;
  border-color: #C02130;
  color: #fff;
}

/* ─── Loading ──────────────────────────────────────────────────── */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 20px;
  gap: 16px;
  color: #888;
  position: relative;
  z-index: 2;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #eee;
  border-top-color: #C02130;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.empty-state {
  padding: 60px;
  text-align: center;
  color: #999;
  position: relative;
  z-index: 2;
}

/* ─── Staff Grid ───────────────────────────────────────────────── */
.staff-grid {
  max-width: 1100px;
  margin: 0 auto;
  /* CRITICAL FIX: use positive top padding, never negative margins */
  padding: 32px 24px 60px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  /* CRITICAL FIX: ensure grid sits above nothing, stack order is natural */
  position: relative;
  z-index: 2;
}

/* ─── Staff Card ───────────────────────────────────────────────── */
.staff-card {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  border: 1px solid #eee;
  transition: transform 0.3s, box-shadow 0.3s, border-color 0.3s;
  animation: fadeUp 0.5s ease both;
  /* CRITICAL FIX: cards must not escape their grid cell */
  position: relative;
  z-index: 2;
}

.staff-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(0,0,0,0.12);
  border-color: #C02130;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ─── Card Photo ───────────────────────────────────────────────── */
.card-photo {
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #f5f5f5;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.photo-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center top;
  transition: transform 0.3s;
  display: block;
}

.staff-card:hover .photo-img {
  transform: scale(1.03);
}

.photo-placeholder {
  width: 100%;
  aspect-ratio: 1 / 1;
  background: linear-gradient(135deg, #C02130, #ff9800);
  display: flex;
  align-items: center;
  justify-content: center;
}

.photo-placeholder span {
  color: #fff;
  font-size: 42px;
  font-weight: 800;
  text-shadow: 0 2px 6px rgba(0,0,0,0.2);
}

/* ─── Card Info ────────────────────────────────────────────────── */
.card-info {
  padding: 20px;
}

.member-name {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 4px;
}

.member-name-ar {
  font-size: 14px;
  color: #777;
  direction: rtl;
  margin: 0 0 8px;
  font-family: 'Arial', sans-serif;
}

.member-role {
  font-size: 14px;
  color: #C02130;
  font-weight: 600;
  margin: 0 0 2px;
}

.member-dept {
  font-size: 12px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px;
}

.member-bio {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 12px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.member-contacts {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.contact-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #555;
  text-decoration: none;
  transition: color 0.2s;
}

.contact-link:hover { color: #C02130; }

.contact-link.email {
  color: #4A7BA7;
  word-break: break-all;
}

.contact-link.email:hover { color: #2c5282; }

/* ─── Responsive ───────────────────────────────────────────────── */
@media (max-width: 768px) {
  .staff-grid {
    grid-template-columns: 1fr;
    padding: 24px 16px 40px;
  }
  .banner {
    padding: 40px 20px;
  }
}

@media (max-width: 480px) {
  /* card-photo uses aspect-ratio so no fixed height needed */
}
</style>